import { OrderInterface } from '../OrdersTable/interfaces/Order.interface';
import { GeoService, toRouteDto } from '../../services/geo.services';
import { Point } from '../../services/orders.services';
import {
	TruckRouteDto,
	TruckTelematicsDto,
} from '../../services/truck.services';
import {
	FleetLocationHistoryDto,
	LastKnownLocationDto,
} from '../../services/tracking.service';
import { toLatLng } from '../../containers/Public/OrderTracking/orderTracking.mapper';
import {
	LIVE_STALE_MS,
	LIVE_STOPPED_KMH,
	LivePoint,
	LiveTrackingService,
} from '../../services/liveTracking.services';

export interface LivePosition {
	lat: number;
	lng: number;
	/** km/h, when the driver app sent it with the fix */
	speedKmh?: number;
	/** ISO time of the GPS fix, when known */
	recordedAt?: string;
	/** smoothed speed while driving, see {@link withAverageSpeed} */
	avgSpeedKmh?: number;
	/** fixes received live, appended to the GPS trail of /tracking */
	trail?: LiveTrailPoint[];
}

export interface LiveTrailPoint {
	lat: number;
	lng: number;
	/** epoch ms */
	at: number;
}

/** About 2.5 h of points at one every ~2 s; older ones are in /tracking. */
export const LIVE_TRAIL_MAX_POINTS = 5000;

/** Weight of a new point in the smoothed speed (points come every ~2 s). */
export const LIVE_SPEED_SMOOTHING = 0.05;

/** Re-route the truck to the pickup at most once a minute. */
export const APPROACH_REFETCH_MIN_INTERVAL_MS = 60000;
/** ...and only when it actually moved. */
export const APPROACH_REFETCH_KM = 0.3;

export type LiveLegTarget = 'pickup' | 'delivery';

export interface ApproachFetch {
	position: { lat: number; lng: number };
	at: number;
	/** fetched from a fresh GPS fix, not the driver's stored location */
	live?: boolean;
	/** where the leg was routed to; a pickup leg is replaced once loaded */
	target?: LiveLegTarget;
}

const distanceKm = (
	a: { lat: number; lng: number },
	b: { lat: number; lng: number },
): number => {
	const rad = (value: number) => (value * Math.PI) / 180;
	const dLat = rad(b.lat - a.lat);
	const dLng = rad(b.lng - a.lng);
	const h =
		Math.sin(dLat / 2) ** 2 +
		Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
	return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

/**
 * Every live fix would otherwise trigger a paid routing request; the route
 * is refreshed only when the truck moved and the last request is at least a
 * minute old. The first fresh GPS fix replaces a route drawn from the
 * driver's stored location (often the fleet base) right away.
 */
export const shouldRefetchApproach = (
	last: ApproachFetch | undefined,
	position: { lat: number; lng: number },
	now: number = Date.now(),
	live: boolean = false,
	target: LiveLegTarget = 'pickup',
): boolean =>
	!last ||
	(last.target ?? 'pickup') !== target ||
	(live && !last.live) ||
	(now - last.at >= APPROACH_REFETCH_MIN_INTERVAL_MS &&
		distanceKm(last.position, position) >= APPROACH_REFETCH_KM);

const toNumber = (value: unknown): number | undefined => {
	if (value === undefined || value === null || value === '') return undefined;
	const number = typeof value === 'number' ? value : parseFloat(String(value));
	return Number.isFinite(number) ? number : undefined;
};

/** Reads a `truck_update_location` socket payload. */
export const parseLivePosition = (
	content: Record<string, any> | undefined,
): { driverId: string; position: LivePosition } | undefined => {
	const driverId = content?.driverId;
	const lat = toNumber(content?.lat);
	const lng = toNumber(content?.long);
	if (!driverId || lat === undefined || lng === undefined) return undefined;
	const speedKmh = toNumber(content?.speed);
	const recordedAt =
		typeof content?.recordedAt === 'string' ? content.recordedAt : undefined;
	return {
		driverId: String(driverId),
		position: {
			lat,
			lng,
			...(speedKmh !== undefined ? { speedKmh } : {}),
			...(recordedAt ? { recordedAt } : {}),
		},
	};
};

/** Reads a `live_location` point of the order watch. */
export const fromLivePoint = (
	point: LivePoint,
	now: number = Date.now(),
): { driverId: string; position: LivePosition } | undefined => {
	if (
		!point?.driverId ||
		!Number.isFinite(point.lat) ||
		!Number.isFinite(point.long)
	) {
		return undefined;
	}
	const speedKmh = LiveTrackingService.displaySpeed(point, now);
	const recordedAt = Number.isFinite(point.recordedAt)
		? new Date(point.recordedAt).toISOString()
		: undefined;
	return {
		driverId: String(point.driverId),
		position: {
			lat: point.lat,
			lng: point.long,
			...(speedKmh !== undefined ? { speedKmh } : {}),
			...(recordedAt ? { recordedAt } : {}),
		},
	};
};

/**
 * The live watch and the location notifications both feed the same driver;
 * a fix older than the one already shown must not move the marker back.
 */
export const isOlderFix = (
	current: LivePosition | undefined,
	next: LivePosition,
): boolean =>
	!!current?.recordedAt &&
	!!next.recordedAt &&
	Date.parse(next.recordedAt) < Date.parse(current.recordedAt);

/**
 * The ETA must not jump with every red light or overtake, so the speed is
 * averaged over the last minute or so of driving; stops leave it untouched,
 * the ETA then slides later because the truck does not get any closer.
 */
export const withAverageSpeed = (
	current: LivePosition | undefined,
	next: LivePosition,
): LivePosition => {
	const previous = current?.avgSpeedKmh;
	const speed = next.speedKmh;
	if (speed === undefined || speed < LIVE_STOPPED_KMH) {
		return previous !== undefined ? { ...next, avgSpeedKmh: previous } : next;
	}
	return {
		...next,
		avgSpeedKmh:
			previous === undefined
				? speed
				: previous + LIVE_SPEED_SMOOTHING * (speed - previous),
	};
};

/** Carries the live trail over to the newer fix and appends that fix. */
export const withLiveTrail = (
	current: LivePosition | undefined,
	next: LivePosition,
	now: number = Date.now(),
): LivePosition => {
	const trail = current?.trail ?? [];
	const last = trail[trail.length - 1];
	if (last && last.lat === next.lat && last.lng === next.lng) {
		return { ...next, trail };
	}
	const parsed = next.recordedAt ? Date.parse(next.recordedAt) : NaN;
	const point = {
		lat: next.lat,
		lng: next.lng,
		at: Number.isFinite(parsed) ? parsed : now,
	};
	return {
		...next,
		trail: trail.concat(point).slice(-LIVE_TRAIL_MAX_POINTS),
	};
};

/**
 * The /tracking trail ends at its last poll; the live fixes after that time
 * extend it, so the driven path follows the truck in real time.
 */
const extendTrail = (
	telematics: TruckTelematicsDto,
	live: LiveTrailPoint[] | undefined,
): TruckTelematicsDto => {
	const trail = telematics.trail ?? [];
	const trailAt = telematics.trailAt;
	const timed = !!trailAt && trailAt.length === trail.length;
	const lastAt =
		timed && trailAt.length ? trailAt[trailAt.length - 1] : undefined;
	const extra = (live ?? []).filter(
		(point) => lastAt === undefined || point.at > lastAt,
	);
	if (!extra.length) return telematics;
	return {
		...telematics,
		trail: trail.concat(
			extra.map((point) => [point.lat, point.lng] as [number, number]),
		),
		trailAt:
			timed || !trail.length
				? (trailAt ?? []).concat(extra.map((point) => point.at))
				: undefined,
	};
};

export type LivePositions = Record<string, LivePosition>;

export const fetchOrderRoute = async (
	pickupLocation?: Point,
	deliveryLocation?: Point,
): Promise<TruckRouteDto | undefined> => {
	const pickup = toLatLng(pickupLocation);
	const delivery = toLatLng(deliveryLocation);
	if (!pickup || !delivery) return undefined;

	const response = await GeoService.getRoute({
		points: [`${pickup.lat},${pickup.lng}`, `${delivery.lat},${delivery.lng}`],
	});
	return toRouteDto(response);
};

export const withOrderRoute = (
	order: OrderInterface,
	route?: TruckRouteDto,
): OrderInterface =>
	route
		? {
				...order,
				trucks: order.trucks?.map((truck) =>
					truck.route ? truck : { ...truck, route },
				),
		  }
		: order;

export type TruckTelematicsById = Record<number, TruckTelematicsDto>;

/** Indexes the `/orders/:id/tracking` answer by truck id. */
/**
 * Trucks whose driver app is streaming positions right now: a live socket
 * point newer than the last "live mode off" signal and not older than
 * {@link LIVE_STALE_MS}. Only these get a route ahead, speed and ETA.
 */
export const streamingTruckIds = (
	trucks: { id?: number; driver?: { uid?: string } }[] | undefined,
	positions: LivePositions,
	liveOffAt: number | undefined,
	now: number = Date.now(),
): Set<number> => {
	const ids = new Set<number>();
	(trucks ?? []).forEach((truck) => {
		const uid = truck.driver?.uid;
		const at = uid ? positions[uid]?.recordedAt : undefined;
		const time = at ? Date.parse(at) : NaN;
		if (
			truck.id !== undefined &&
			Number.isFinite(time) &&
			now - time <= LIVE_STALE_MS &&
			time > (liveOffAt ?? 0)
		) {
			ids.add(truck.id);
		}
	});
	return ids;
};

const fixTime = (value?: string): number => {
	const time = value ? Date.parse(value) : NaN;
	return Number.isFinite(time) ? time : 0;
};

/**
 * The driver app state and last fix as telematics. Only a fix with a capture
 * time counts: without one the backend is handing back the driver's stored
 * location (the fleet base), which never places the truck.
 */
export const lastLocationToTelematics = (
	last: LastKnownLocationDto | undefined,
): TruckTelematicsDto | undefined => {
	if (
		!last ||
		!Number.isFinite(last.lat) ||
		!Number.isFinite(last.long) ||
		!fixTime(last.recordedAt)
	) {
		return undefined;
	}
	return {
		position: { lat: last.lat, lng: last.long },
		speedKmh: last.speedKmh,
		recordedAt: last.recordedAt!,
		idleSince: null,
		trail: [],
		lastSeenAt: last.lastSeenAt,
		isOnline: last.isOnline,
		address: last.address,
	};
};

/** The breadcrumb trail of a truck as telematics: trail, times, last fix. */
export const historyToTelematics = (
	history: FleetLocationHistoryDto | undefined,
): TruckTelematicsDto | undefined => {
	const items = (history?.items ?? [])
		.filter(
			(item) =>
				Number.isFinite(item?.lat) &&
				Number.isFinite(item?.long) &&
				Number.isFinite(item?.timestamp),
		)
		.sort((a, b) => a.timestamp - b.timestamp);
	const last = lastLocationToTelematics(history?.lastLocation);
	if (!items.length) return last;
	const newest = items[items.length - 1];
	const fromItems: TruckTelematicsDto = {
		position: { lat: newest.lat, lng: newest.long },
		speedKmh: newest.speed,
		recordedAt: new Date(newest.timestamp).toISOString(),
		idleSince: null,
		trail: items.map((item) => [item.lat, item.long] as [number, number]),
		trailAt: items.map((item) => item.timestamp),
	};
	return mergeTelematics(fromItems, last);
};

/**
 * One telematics record out of two sources: the newer fix wins, the longer
 * trail wins, and the driver app state comes from whichever has it.
 */
export const mergeTelematics = (
	a: TruckTelematicsDto | undefined,
	b: TruckTelematicsDto | undefined,
): TruckTelematicsDto | undefined => {
	if (!a) return b;
	if (!b) return a;
	const newer = fixTime(b.recordedAt) > fixTime(a.recordedAt) ? b : a;
	const older = newer === a ? b : a;
	const longerTrail = (b.trail?.length ?? 0) > (a.trail?.length ?? 0) ? b : a;
	return {
		...older,
		...newer,
		trail: longerTrail.trail ?? [],
		trailAt: longerTrail.trailAt,
		lastSeenAt:
			fixTime(b.lastSeenAt) > fixTime(a.lastSeenAt)
				? b.lastSeenAt
				: a.lastSeenAt ?? b.lastSeenAt,
		isOnline: newer.isOnline ?? older.isOnline,
		address: newer.address ?? older.address,
	};
};

export const toTelematicsById = (
	trucks:
		| {
				id: number;
				telematics?: TruckTelematicsDto;
				lastLocation?: LastKnownLocationDto;
		  }[]
		| undefined,
): TruckTelematicsById =>
	(trucks ?? []).reduce<TruckTelematicsById>((byId, truck) => {
		const merged = mergeTelematics(
			truck?.telematics,
			lastLocationToTelematics(truck?.lastLocation),
		);
		if (merged) byId[truck.id] = merged;
		return byId;
	}, {});

/**
 * Hangs the GPS history (trail, last fix, speed, idling) on the order's
 * trucks, the way the public order link carries it, so the order pages draw
 * the driven route and step checkpoints like track.sypac.app.
 */
export const withTelematics = (
	order: OrderInterface,
	telematics: TruckTelematicsById,
): OrderInterface =>
	Object.keys(telematics).length && order.trucks?.length
		? {
				...order,
				trucks: order.trucks.map((truck) =>
					truck.id !== undefined && telematics[truck.id]
						? { ...truck, telematics: telematics[truck.id] }
						: truck,
				),
		  }
		: order;

export const withLivePositions = (
	order: OrderInterface,
	positions: LivePositions,
): OrderInterface => {
	if (!Object.keys(positions).length || !order.trucks?.length) return order;

	return {
		...order,
		trucks: order.trucks.map((truck) => {
			const driver = truck.driver;
			const position = driver?.uid ? positions[driver.uid] : undefined;
			if (!position) return truck;
			const seenAt = position.recordedAt ?? new Date().toISOString();
			// Telematics win over the driver's last location in the mapper, so
			// they have to move with the live fix too (and carry its speed).
			const telematics =
				truck.telematics || position.speedKmh !== undefined
					? {
							...truck.telematics,
							position: { lat: position.lat, lng: position.lng },
							speedKmh: position.speedKmh ?? truck.telematics?.speedKmh,
							avgSpeedKmh:
								position.avgSpeedKmh ?? truck.telematics?.avgSpeedKmh,
							recordedAt: seenAt,
							idleSince:
								position.speedKmh && position.speedKmh > 0
									? null
									: truck.telematics?.idleSince ?? null,
							trail: truck.telematics?.trail ?? [],
					  }
					: undefined;
			return {
				...truck,
				...(telematics
					? { telematics: extendTrail(telematics, position.trail) }
					: {}),
				driver: {
					...driver,
					lastSeenAt: seenAt,
					lastLocation: {
						type: 'Point',
						coordinates: [position.lng, position.lat],
					},
				},
			};
		}),
	};
};
